<template>
    <modal
        title="Dropdown Options"
        class="select-options"
        header-bg-variant="blue-600"
        header-text-variant="white"
        :hide-header-close="true"
        @ok="onConfirm"
        @hide="onCancel"
    >
        <div class="select-options__switch">
            <input
                v-model="dynamicDataChecked"
                type="checkbox"
                @changed="onDynamicChanged"
            >
            Use Dynamic Data
        </div>

        <div v-if="dynamicDataChecked" class="select-options__content">
            <header class="select-options__header">
                <div class="select-options__col">
                    <span class="select-options__label">Datasource ID</span>
                </div>

                <div class="select-options__col">
                    <span class="select-options__label">
                        Text
                        <tooltip>
                            Please use <a href="https://restfulapi.net/json-jsonpath/" target="_blank">JSONPath</a> for this field.
                        </tooltip>
                    </span>
                </div>

                <div class="select-options__col">
                    <span class="select-options__label">
                        Value
                        <tooltip>
                            Please use <a href="https://restfulapi.net/json-jsonpath/" target="_blank">JSONPath</a> for this field.
                        </tooltip>
                    </span>
                </div>
            </header>

            <ul class="select-options__list">
                <li class="select-options__item">
                    <div class="select-options__col">
                        <input
                            v-model="datasourceIdInput"
                            class="select-options__input"
                        >
                    </div>

                    <div class="select-options__col">
                        <input
                            v-model="datasourceTextInput"
                            class="select-options__input"
                        >
                    </div>

                    <div class="select-options__col">
                        <input
                            v-model="datasourceValueInput"
                            class="select-options__input"
                        >
                    </div>
                </li>
            </ul>
        </div>

        <form
            v-else
            ref="form"
            class="select-options__content"
            @submit.prevent
        >
            <header class="select-options__header">
                <div class="select-options__col">
                    <span class="select-options__label">Display Name</span>
                </div>

                <div class="select-options__col">
                    <span class="select-options__label">Value</span>
                </div>
            </header>

            <ul class="select-options__list">
                <li v-for="(option, i) in options" :key="i" class="select-options__item">
                    <div class="select-options__col">
                        <input
                            class="select-options__input"
                            type="text"
                            placeholder="Add text"
                            :value="option.text"
                            required
                            @input="(e) => onInputName(e, i)"
                        >
                    </div>

                    <div class="select-options__col">
                        <input
                            class="select-options__input"
                            type="text"
                            placeholder="Add text"
                            :value="option.value"
                            required
                            @input="(e) => onInputValue(e, i)"
                        >
                        <button class="select-options__delete" @click="deleteRow(i)">
                            <font-awesome-icon
                                :icon="['fal', 'trash-alt']"
                            />
                        </button>
                    </div>
                </li>
            </ul>

            <button class="select-options__add" type="button" @click="onAddOption">
                Add Option
            </button>
        </form>

        <template v-slot:modal-footer="slotProps">
            <button class="flex-modal__button flex-modal__button--cancel" @click="slotProps.hide('cancel')">
                Cancel
            </button>

            <button class="flex-modal__button flex-modal__button--continue" @click="onConfirm">
                Update
            </button>
        </template>
    </modal>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { HIDE_MODAL, SET_CHANGED_DATA } from '@/store/types/mutation-types';

export default {
    data() {
        return {
            isValid: true,
            options: this.getDefaultOptions(),
            dynamicDataChecked: true,
            datasourceIdInput: '',
            datasourceTextInput: '$.name',
            datasourceValueInput: '$.fields[0].value',
        };
    },

    computed: {
        ...mapState({
            selectedElements: state => state.page.selectedElements,
        }),

        existingOptions() {
            return !!Object.keys(this.selectedElements[0].optionsNew).length;
        },
    },

    watch: {
        selectedElements() {
            this.onSelectedElement();
        },
    },

    methods: {
        ...mapMutations({
            hideModal: HIDE_MODAL,
            setChangedData: SET_CHANGED_DATA,
        }),

        getDefaultOptions() {
            return [
                { text: '', value: '' },
            ];
        },

        deleteRow(index) {
            this.options.splice(index, 1);
        },

        onDynamicChanged() {
            // reset id so it wont override other options
            if (!this.dynamicDataChecked) {
                this.datasourceIdInput = '';
            }
        },

        onInputName(event, index) {
            this.options[index].text = event.target.value;
        },

        onInputValue(event, index) {
            this.options[index].value = event.target.value;
        },

        onAddOption() {
            const lastOption = this.options[this.options.length - 1];

            if (!lastOption.text || !lastOption.value) {
                this.validate();
                return;
            }

            this.options.push({ text: '', value: '' });
        },

        onConfirm() {
            if (this.dynamicDataChecked) {
                this.selectedElements[0].setElementData('selectDatasource', this.datasourceIdInput);
                this.selectedElements[0].setElementData('selectTextPath', this.datasourceTextInput);
                this.selectedElements[0].setElementData('selectValuePath', this.datasourceValueInput);
                this.setChangedData(true);
                this.hideModal();
            } else {
                this.validate();

                if (this.isValid) {
                    this.selectedElements[0].optionsNew = this.serializeOptions(this.options);
                    this.setChangedData(true);
                    this.hideModal();
                }
            }
        },

        onCancel() {
            this.options = this.existingOptions ? this.deserializeOptions(this.selectedElements[0].optionsNew) : this.getDefaultOptions();
        },

        onSelectedElement() {
            const existingOptions = this.selectedElements[0] && this.selectedElements[0].optionsNew;

            if (existingOptions) {
                this.options = this.deserializeOptions(existingOptions);
            }

            const existingDSId = this.selectedElements[0] && this.selectedElements[0].getElementData('selectDatasource');
            if (existingDSId) {
                this.datasourceIdInput = existingDSId;
            }
        },

        validate() {
            this.isValid = this.$refs.form.reportValidity();
        },

        serializeOptions(options) {
            return options.reduce((nvList, option) => {
                nvList[option.text] = option.value;

                return nvList;
            }, {});
        },

        deserializeOptions(options) {
            if (!this.existingOptions) {
                return this.getDefaultOptions();
            }

            return Object.entries(options).map((entry) => {
                return {
                    text: entry[0],
                    value: entry[1],
                };
            });
        },
    },
};
</script>

<style lang="scss">
.select-options {
    &__switch {
        display: flex;
        align-items: center;
        padding: 1.5rem 1.5rem 0 1.5rem;

        input {
            position: relative;
            top: -1px;
            margin-right: 0.75em;
        }
    }

    &__content {
        padding: 1.5rem;
    }

    &__section {
        width: 100%;
        margin-bottom: 0.75em;
    }

    &__header {
        display: flex;
        margin-bottom: 0.75em;
    }

    &__label {
        display: flex;
        flex-direction: row;
        align-items: center;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--form-labelColor);
    }

    &__item {
        display: flex;

        &:not(:first-of-type) {
            margin-top: 0.75rem;
        }
    }

    &__col {
        width: 50%;
        display: flex;

        &:first-child {
            padding-right: 0.5rem;
        }

        &:not(:first-child):not(:last-child) {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
        }

        &:last-child {
            padding-left: 0.5rem;
        }
    }

    &__delete {
        margin-left: 10px;
    }

    &__input {
        @extend %input;
    }

    &__add {
        @extend %button-alt;
        @extend %button-compact;
        margin-top: 1.75rem;
    }
}
</style>
